import React from 'react';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import AhamLogo from '../../img/Aham_logo.png';

import {
  PhoneIcon,
  EnvelopeIcon,
  MapPinIcon,
  ArrowUpIcon
} from '@heroicons/react/24/outline';

import { FaFacebookF, FaTwitter, FaInstagram, FaLinkedinIn, FaYoutube } from "react-icons/fa";
import { getPhoneLink, scrollToSection } from '../../utils/helpers';

const Footer = () => {
  const { t } = useTranslation();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const quickLinks = [
    { label: 'Home Loans', href: '/home-construction-loan-india' },
    { label: 'PMAY 2.0', href: '/pmay-2-0-scheme-apply-online' },
    { label: 'EMI Calculator', href: '/home-loan-emi-calculator' },
    // { label: 'Eligibility Check', href: '/customer-360-eligibility-calculator' },
    { label: 'FAQ', href: '/home-loan-faq' },
    { label: 'Risk Classification', href: '/risk-classification-framework-policy' },
    { label: 'About Us', href: '/about-aham-housing-finance' },
    { label: 'Contact', href: '/contact-aham-housing-finance' },
    { label: t('footer.resources') || 'Resources', href: '/resources' },
   // { label: 'GMB', href: '/affordable-home-loan-in-madurai' }
    //{ label: 'Festival Campaign', href: '/festival-campaign' }
  ];

  const products = [
    { label: 'Home Construction Loan', href: '/home-construction-loan-india' },
    { label: 'Home Purchase or Resale Loan', href: '/home-purchase-loan' },
    { label: 'Composite Loan', href: '/composite-home-loan-plot-construction' },
    { label: 'Home Renovation Loan', href: '/home-renovation-loan' },
    { label: 'Home Extension Loan', href: '/home-extension-loan' },
    { label: 'Balance Transfer Loan', href: '/home-loan-balance-transfer' },
    { label: 'NRI Home Loan', href: '/nri-home-loan-india' },
    { label: 'Top-up Loan', href: '/top-up-loan-for-home-loan' },
    { label: 'Mortgage Loan', href: '/loan-against-property-mortgage-loan' }
  ];

  const socialLinks = [
    { name: 'Facebook', href: 'https://www.facebook.com/ahamhfc', icon: <FaFacebookF /> },
    //{ name: 'Twitter', href: 'https://twitter.com', icon: <FaTwitter /> },
    { name: 'Instagram', href: 'https://www.instagram.com/aham_housingfinance/', icon: <FaInstagram /> },
    { name: 'LinkedIn', href: 'https://linkedin.com/company/ahamhfc?originalSubdomain=in', icon: <FaLinkedinIn /> },
    { name: 'YouTube', href: 'https://www.youtube.com/@ahamhfc', icon: <FaYoutube /> }
  ];

  return (
    <footer className="bg-gray-900 text-white relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #283079 0%, transparent 50%),
                           radial-gradient(circle at 75% 75%, #4f46e5 0%, transparent 50%)`
        }} />
      </div>

      <div className="container mx-auto px-4 relative">
        <div className="py-16">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">

            {/* Company Info */}
            <div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <Link to="/" className="flex items-center mb-6 cursor-pointer"
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
                  <img src={AhamLogo} alt="AHAM Housing Finance Limited Logo" className="w-40 h-auto object-contain" />
                </Link>

                <p className="text-gray-300 mb-6 leading-relaxed max-w-md">
                  {t('footer.description')}
                </p>

                <p className="leading-relaxed">
                  (formerly known as Aham Housing Finance Private Limited)
                </p><br></br>

                <div className="space-y-3">
                  <div className="flex items-center space-x-3">
                    <PhoneIcon className="h-5 w-5 text-primary-400 flex-shrink-0" />
                    <a href={getPhoneLink()} className="text-gray-300 hover:text-red-600 transition-colors">
                      {t('footer.phone')}
                    </a>
                  </div>

                  <div className="flex items-center space-x-3">
                    <EnvelopeIcon className="h-5 w-5 text-primary-400 flex-shrink-0" />
                    <a href="mailto:info@ahamhfc.com" className="text-gray-300 hover:text-red-600 transition-colors">
                      {t('footer.email')}
                    </a>
                  </div>

                  <div className="flex items-start space-x-3">
                    <MapPinIcon className="h-5 w-5 text-primary-400 flex-shrink-0 mt-1" />
                    <span className="text-gray-300">{t('footer.address')}</span>
                  </div>
                </div>

                {/* Social Icons */}
                <div className="flex space-x-4 mt-6">
                  {socialLinks.map((social, index) => (
                    <motion.a
                      key={social.name}
                      href={social.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="bg-gray-800 hover:bg-primary-600 text-gray-400 hover:text-white w-10 h-10 rounded-lg transition-all duration-300 flex items-center justify-center text-xl"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      {social.icon}
                    </motion.a>
                  ))}
                </div>
              </motion.div>
            </div>

            {/* Quick Links */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }} viewport={{ once: true }}>
              <p className="text-lg text-white font-semibold mb-6">{t('footer.quickLinks')}</p>
              <ul className="space-y-3">
                {quickLinks.map((link, index) => (
                  <li key={index}>
                    <a href={link.href} className="text-gray-300 hover:text-red-600 transition-colors duration-300">
                      {link.label}
                    </a>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Products */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.4 }} viewport={{ once: true }}>
              <p className="text-lg text-white font-semibold mb-6">{t('footer.products')}</p>
              <ul className="space-y-3">
                {products.map((product, index) => (
                  <li key={index}>
                    <Link to={product.href} className="text-gray-300 hover:text-red-600 transition-colors duration-300">
                      {product.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </motion.div>

            {/* Corporate and Registered Office - 4th Column */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.5 }} viewport={{ once: true }}>
              <p className="text-lg text-white font-semibold mb-6">Registered and Corporate office</p>
              <div className="space-y-4 text-gray-300">
                <div>
                  <p className="font-semibold text-primary-400 mb-2">AHAM HOUSING FINANCE LIMITED.,</p>
                  <p className="leading-relaxed text-gray-300 hover:text-red-600 transition-colors">
                    New No.2 &4, Old No.24 &25,<br />
                    3rd Floor, Sivaganga Road,<br />
                    Nungambakkam, Chennai - 600034.
                  </p>
                </div>
              </div>
            </motion.div>

            {/* Statutory Auditors - 5th Column */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.6 }} viewport={{ once: true }}>
              <p className="text-lg text-white font-semibold mb-6">Statutory Auditors</p>
              <div className="space-y-4 text-gray-300">
                <div>
                  <p className="font-semibold text-primary-400 mb-2">R Subramanian & Company LLP</p>
                  <p className="leading-relaxed">
                    6 Krishnaswamy Avenue, Mylapore<br />
                    Chennai, 600 004-India
                  </p>
                 {/* <a href="http://www.rscompany.in" target="_blank" rel="noopener noreferrer" className="text-primary-400 hover:text-primary-300 transition-colors">
                    www.rscompany.in
                  </a> */}
                   <p className="text-primary-400">
                    www.rscompany.in
                  </p>
                </div>

                <div className="pt-4 border-t border-gray-700">
                  {/* <p className="font-semibold text-white mb-2">AHAM HOUSING FINANCE LIMITED</p>
                  <p className="leading-relaxed">
                    (formerly known as Aham Housing Finance Private Limited)
                  </p> */}
                  <div className="mt-3 space-y-1">
                    <p>CIN No: U65922TN2017PLC153989</p>
                    <p>GST No: 33AAQCA2497L1ZS (Tamilnadu)</p>
                    <p>GST No: 37AAQCA2497L1ZX (AndhraPradesh)</p>
                    <p>GST No: 29AAQCA2497L1ZU (Karnataka)</p>
                  </div>
                </div>
              </div>
            </motion.div>

          </div>
        </div>

        {/* Bottom Bar */}
        <div className="border-t border-gray-800 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
            {/* <p className="text-gray-400 text-sm">{t('footer.copyright')}</p> */}
            <p className="text-gray-400 text-sm">© 2026 AHAM Housing Finance Limited. Powered by <a href="https://mbwit.net" target="_blank" rel="noopener noreferrer" className="text-red-600 hover:text-white transition-colors duration-300">MBW</a></p>

            <div className="flex items-center space-x-6 text-sm">
              <a href="/privacy-policy" className="text-gray-400 hover:text-white">Privacy Policy</a>
              {/* <a href="/terms" className="text-gray-400 hover:text-white">Terms</a> */}
              <a href="/cookies" className="text-gray-400 hover:text-white">Cookie Policy</a>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      <motion.button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 bg-primary-600 hover:bg-primary-700 text-white p-3 rounded-full shadow-lg z-[70]"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        <ArrowUpIcon className="h-6 w-6" />
      </motion.button>
    </footer>
  );
};

export default Footer;
